import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import {
  ThumbsUpIcon,
  ThumbsDownIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useMyFeedbackForSubject } from '@/api/rpc/hooks/core/user-feedback/useMyFeedbackForSubject';
import { useSubmitFeedback } from '@/api/rpc/hooks/core/user-feedback/useSubmitFeedback';
import { useDeleteFeedback } from '@/api/rpc/hooks/core/user-feedback/useDeleteFeedback';
import { FeedbackCommentField } from '@/components/feedback/FeedbackCommentField';
import { NAMI_CHAT_THUMBS } from '@erp/shared-types';

const SUBJECT_TYPE = NAMI_CHAT_THUMBS.subjectType;
const SCORE_NAME = NAMI_CHAT_THUMBS.scoreName;

interface ChatMessageActionsProps {
  dbMessageId: string;
  traceId: string | null;
  observationId: string | null;
}

export function ChatMessageActions({
  dbMessageId,
  traceId,
  observationId,
}: ChatMessageActionsProps) {
  const t = useGT();
  const { feedbackByScoreName } = useMyFeedbackForSubject({
    subjectType: SUBJECT_TYPE,
    subjectId: dbMessageId,
  });
  const submit = useSubmitFeedback();
  const remove = useDeleteFeedback();

  const existing = feedbackByScoreName?.[SCORE_NAME] ?? null;
  const value = existing?.booleanValue ?? null;
  const savedComment = existing?.comment ?? '';

  const [draft, setDraft] = useState(savedComment);
  const [expanded, setExpanded] = useState(false);

  // Keep draft in sync with the server-side comment when it changes
  // (initial load, refetch after submit, switching between threads).
  useEffect(() => {
    setDraft(savedComment);
  }, [savedComment]);

  const handleThumbClick = (next: boolean) => {
    if (value === next) {
      // Clear: clicking the active thumb removes the score.
      if (existing) {
        remove.mutate({
          feedbackId: existing.id,
          subjectType: SUBJECT_TYPE,
          subjectId: dbMessageId,
        });
      }
      setExpanded(false);
      return;
    }
    submit.mutate({
      subjectType: SUBJECT_TYPE,
      subjectId: dbMessageId,
      scoreName: SCORE_NAME,
      value: { dataType: 'BOOLEAN', booleanValue: next },
      comment: draft.trim() ? draft.trim() : null,
      traceId,
      observationId,
    });
    setExpanded(true);
  };

  const handleSendComment = () => {
    if (value === null) return;
    submit.mutate({
      subjectType: SUBJECT_TYPE,
      subjectId: dbMessageId,
      scoreName: SCORE_NAME,
      value: { dataType: 'BOOLEAN', booleanValue: value },
      comment: draft.trim() ? draft.trim() : null,
      traceId,
      observationId,
    });
    setExpanded(false);
  };

  const isUp = value === true;
  const isDown = value === false;
  const hasSavedComment = !!savedComment.trim();
  const showCommentRow = expanded;

  return (
    <div className="flex flex-col items-start gap-1">
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-7 w-7 text-stone-500 hover:text-stone-800',
                  isUp && 'text-emerald-600 hover:text-emerald-700',
                )}
                aria-label={t('Helpful')}
                aria-pressed={isUp}
                onClick={() => handleThumbClick(true)}
              >
                <ThumbsUpIcon
                  className="h-4 w-4"
                  weight={isUp ? 'fill' : 'regular'}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <T>Helpful</T>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-7 w-7 text-stone-500 hover:text-stone-800',
                  isDown && 'text-rose-600 hover:text-rose-700',
                )}
                aria-label={t('Not helpful')}
                aria-pressed={isDown}
                onClick={() => handleThumbClick(false)}
              >
                <ThumbsDownIcon
                  className="h-4 w-4"
                  weight={isDown ? 'fill' : 'regular'}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <T>Not helpful</T>
            </TooltipContent>
          </Tooltip>

          {value !== null && !showCommentRow && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-stone-500 hover:text-stone-800"
                  aria-label={
                    hasSavedComment ? t('Edit note') : t('Add a note')
                  }
                  onClick={() => setExpanded(true)}
                >
                  <PencilSimpleIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {hasSavedComment ? <T>Edit note</T> : <T>Add a note</T>}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>

      {showCommentRow && (
        <div className="w-full max-w-md space-y-2">
          <FeedbackCommentField
            value={draft}
            onChange={setDraft}
            placeholder={t('Tell us more (optional)')}
          />
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/feedback"
              className="text-xs text-stone-500 hover:text-stone-800 hover:underline"
            >
              <T>View my feedback</T>
            </Link>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(savedComment);
                  setExpanded(false);
                }}
              >
                <T>Cancel</T>
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSendComment}
                disabled={submit.isPending || draft === savedComment}
              >
                <T>Save</T>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
